import { useEffect, useState } from "react";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconLogout,
  IconMenu2,
} from "@tabler/icons-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { navigationForRole } from "../app/navigation";
import { useAuth } from "../features/auth/AuthProvider";
import type { UserRole } from "../features/auth/types";

const SIDEBAR_KEY = "infoparafia_panel_sidebar_collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
}

export function PanelLayout({ role }: { role: UserRole }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({
    "/admin/website": location.pathname.startsWith("/admin/website"),
  }));
  const items = navigationForRole(role);

  useEffect(() => {
    setMobileOpen(false);
    if (location.pathname.startsWith("/admin/website")) {
      setOpenGroups((current) =>
        current["/admin/website"]
          ? current
          : { ...current, "/admin/website": true },
      );
    }
  }, [location.pathname]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
    } catch {
      // no-op
    }
  }

  return (
    <div className={`panel-app ${collapsed ? "sidebar-collapsed" : ""}`}>
      <button
        className={`sidebar-backdrop ${mobileOpen ? "is-visible" : ""}`}
        aria-label="Zamknij menu"
        onClick={() => setMobileOpen(false)}
      />
      <aside
        className={`panel-sidebar ${mobileOpen ? "is-open" : ""}`}
        aria-label="Menu główne"
      >
        <div className="sidebar-brand">
          <img src="/ikona-aplikacji.png" alt="" />
          <span className="sidebar-brand-copy">
            <strong>InfoParafia</strong>
            <small>Panel administracyjny</small>
          </span>
        </div>
        <nav className="sidebar-nav">
          {items.map((item, index) => {
            const previousSection =
              index > 0 ? items[index - 1].section : undefined;
            return (
              <div key={item.to}>
                {item.section && item.section !== previousSection ? (
                  <div className="sidebar-section">{item.section}</div>
                ) : null}
                <div className="sidebar-link-row">
                  <NavLink
                    className={({ isActive }) =>
                      `sidebar-link ${isActive || item.children?.some((child) => location.pathname === child.to) ? "active" : ""}`
                    }
                    end={
                      item.to === "/admin" ||
                      item.to === "/superadmin" ||
                      Boolean(item.children)
                    }
                    to={item.to}
                    title={collapsed ? item.label : undefined}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span className="sidebar-label">{item.label}</span>
                  </NavLink>
                  {item.children && !collapsed ? (
                    <button
                      className="sidebar-group-toggle"
                      type="button"
                      aria-label={`${openGroups[item.to] ? "Zwiń" : "Rozwiń"} ${item.label}`}
                      aria-expanded={Boolean(openGroups[item.to])}
                      onClick={() =>
                        setOpenGroups({
                          ...openGroups,
                          [item.to]: !openGroups[item.to],
                        })
                      }
                    >
                      <IconChevronDown size={16} />
                    </button>
                  ) : null}
                </div>
                {item.children && openGroups[item.to] && !collapsed ? (
                  <div className="sidebar-submenu">
                    {item.children.map((child) => (
                      <NavLink
                        className={({ isActive }) =>
                          `sidebar-sublink ${isActive ? "active" : ""}`
                        }
                        end={child.to === item.to}
                        to={child.to}
                        key={child.to}
                      >
                        <span className="sidebar-icon">{child.icon}</span>
                        <span>{child.label}</span>
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
        <button
          className="sidebar-collapse"
          type="button"
          onClick={toggleCollapsed}
        >
          {collapsed ? (
            <IconChevronRight size={18} />
          ) : (
            <IconChevronLeft size={18} />
          )}
          <span className="sidebar-label">Zwiń menu</span>
        </button>
      </aside>

      <div className="panel-main">
        <header className="panel-topbar">
          <button
            className="btn btn-icon btn-ghost-secondary mobile-menu"
            type="button"
            aria-label="Otwórz menu"
            onClick={() => setMobileOpen(true)}
          >
            <IconMenu2 size={22} />
          </button>
          <div className="topbar-context">
            <span className="badge bg-blue-lt">{role}</span>
            <span className="text-secondary">{user?.email || "-"}</span>
          </div>
          <a
            className="btn btn-outline-secondary btn-sm topbar-legacy-link"
            href="/legacy/"
            target="_blank"
            rel="noreferrer"
          >
            Panel klasyczny
          </a>
          <button
            className="btn btn-outline-secondary btn-sm"
            type="button"
            onClick={logout}
          >
            <IconLogout size={17} /> Wyloguj
          </button>
        </header>
        <main className="panel-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
